import React, { useState } from 'react'
import { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDispatch, connect } from 'react-redux'
import ModalActions from 'reducers/Modal'
import NotificationActions from 'reducers/Notification'
import {
  Button,
  MaterialIcon,
  Modal,
  Tonality,
  TextField,
  Banner
} from '@dsny/dsny-component-library'
import { NotificationType } from 'constants/enums'
import {
  useMobileMediaQuery,
  useDesktopMediaQuery
} from 'utils/hooks/useMediaQueries'
import {
  XButtonWrapper,
  Row,
  SelectedConnections
} from './ShareReleaseModal.styles'
import ShareReleaseModalEmptyState from './ShareReleaseModalEmptyState'
import { ConnectionsDropdown } from '../../Connections'
import { thunks as ReleaseThunks } from 'reducers/Releases'
import { ConnectionItem } from 'components/Connections'
import Tooltip from 'components/Tooltip'
import { StyledModal } from '../Modals.styles'
import { trackEvent } from 'utils/segmentService'
import { normalizeValue } from 'utils/analytics'

const MAX_DESCRIPTION_LIMIT = 500

const mapStateToProps = ({ modal, authentication, releases }) => ({
  showShareReleaseModal: modal?.showShareReleaseModal,
  connections: authentication.connections,
  currentReleaseInformation: releases.currentReleaseInformation
})

function ShareReleaseModal({
  showShareReleaseModal,
  connections,
  currentReleaseInformation
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMobile = useMobileMediaQuery()
  const isDesktop = useDesktopMediaQuery()
  const [message, setMessage] = useState('')
  const [messageCount, setMessageCount] = useState(0)
  const [selectedConnections, setSelectedConnections] = useState([])
  const [error, setError] = useState('')

  const [connectionsDropdownError, setConnectionsDropdownError] = useState('')
  const hasAtLeastOneConnectionVerified =
    connections && connections.some(({ verified }) => verified === true)

  const onCancelClick = () => {
    dispatch(ModalActions.toggleShareReleaseModal())
  }

  const onMessageChange = (e) => {
    setMessageCount(e.target.value.length)
    setMessage(e.target.value)
  }

  const onConnectionSelect = (connection) => {
    const newConnectionsList = [...selectedConnections, connection]
    setSelectedConnections(newConnectionsList)
  }

  const onShareReleaseSubmit = async () => {
    if (selectedConnections.length === 0) {
      setConnectionsDropdownError(
        t('share-release.no-connection-selected-error')
      )
      return
    }

    try {
      const playersIdList = selectedConnections.map(
        ({ player_id }) => player_id
      )

      const response = await dispatch(
        ReleaseThunks.shareRelease(
          currentReleaseInformation.releaseid,
          playersIdList,
          message
        )
      )

      if (response.status === 201) {
        dispatch(
          NotificationActions.broadcastMessage(
            '',
            NotificationType.releaseShared
          )
        )

        trackEvent('Release Share', {
          release_id: normalizeValue(currentReleaseInformation?.releaseid),
          artist: normalizeValue(currentReleaseInformation?.artist),
          title: normalizeValue(currentReleaseInformation?.title),
          recipient_count: normalizeValue(selectedConnections.length),
          has_message: normalizeValue(Boolean(message?.trim())),
          source: 'release_information',
          type: 'share'
        })

        dispatch(ModalActions.toggleShareReleaseModal())
      }
    } catch (error) {
      console.error('Error sharing release', error)
      setError(error.data?.error)
    }
  }

  const onDeleteConnection = (id) => {
    const newConnectionsList = selectedConnections.filter(
      (connection) => connection.player_id !== id
    )
    setSelectedConnections(newConnectionsList)
  }

  return (
    <StyledModal
      $isMobile={isMobile}
      isOpen={showShareReleaseModal}
      onClose={onCancelClick}
      containerStyles={{
        padding: isMobile ? '48px 16px 24px' : '64px 64px 64px'
      }}
    >
      <XButtonWrapper onClick={onCancelClick}>
        <MaterialIcon name="X" color={theme.colors.neutralW80} width={24} />
      </XButtonWrapper>

      {error && (
        <Banner
          mode="dark"
          style={{ marginBottom: 32, maxWidth: '100%' }}
          variant="error"
          bannerContent={{
            title: t('share-release.generic-error-title'),
            description: t('share-release.generic-error-body')
          }}
        />
      )}

      {hasAtLeastOneConnectionVerified ? (
        <>
          <div
            style={{
              alignItems: 'center',
              marginBottom: 8,
              display: isMobile ? 'inline-block' : 'flex'
            }}
          >
            <Tonality.UIHeading
              headingStyle="headline5"
              color={theme.colors.neutralW90}
              style={{ marginRight: 8 }}
            >
              {t('share-release.share-release-with-connections')}{' '}
            </Tonality.UIHeading>

            <Tooltip
              hasArrow={false}
              placement={isMobile ? 'top' : 'right'}
              text={t('share-release.player-pro-tooltip')}
              containerStyles={{
                backgroundColor: theme.colors.neutralW90,
                color: theme.colors.neutralW20,
                fontWeight: 700,
                fontSize: '12px',
                maxWidth: !isDesktop ? 190 : 340,
                width: !isDesktop ? 190 : 340,
                whiteSpace: 'normal',
                margin: isMobile ? '-6px 0 0 -50px' : '9px 0 0 8px'
              }}
            >
              <Tonality.Pill
                mode="dark"
                variant="tonal"
                selected
                label="Player PRO"
                style={{
                  border: `1px solid ${theme.colors.player50}`,
                  position: 'relative',
                  marginLeft: isMobile ? '-6px' : 0
                }}
              />
            </Tooltip>
          </div>

          <Tonality.UIText
            textStyle="body"
            color={theme.colors.neutralW70}
            style={{
              marginBottom: isMobile ? 16 : 24,
              fontSize: 14
            }}
          >
            {t('share-release.share-release-with-connections-body')}{' '}
          </Tonality.UIText>

          {!isMobile && (
            <Tonality.UIText
              textStyle="body"
              color={theme.colors.neutralW70}
              style={{
                marginBottom: 24,
                fontSize: 14,
                fontStyle: 'italic'
              }}
            >
              {t('share-release.share-release-with-connections-disclaimer')}{' '}
            </Tonality.UIText>
          )}

          <Row style={{ marginBottom: 16 }}>
            <ConnectionsDropdown
              connections={connections.filter(({ verified }) => verified)}
              selectedConnections={selectedConnections}
              onConnectionSelect={onConnectionSelect}
              error={connectionsDropdownError}
            />
          </Row>

          {selectedConnections.length > 0 && (
            <div>
              <Tonality.UIText
                textStyle="headline6"
                color={theme.colors.neutralW90}
              >
                {`${t('share-release.selected-for-sharing')} (${
                  selectedConnections.length
                })`}
              </Tonality.UIText>

              <SelectedConnections style={{ maxHeight: isMobile ? 115 : 270 }}>
                {selectedConnections.map((connection) => (
                  <ConnectionItem
                    key={connection.player_id}
                    connection={connection}
                    action="Remove"
                    style={{ marginBottom: 8 }}
                    onDeleteConnection={onDeleteConnection}
                  />
                ))}
              </SelectedConnections>
            </div>
          )}

          <Row style={{ alignItems: 'center', marginBottom: 4 }}>
            <Tonality.UIText
              textStyle="headline6"
              color={theme.colors.neutralW90}
            >
              {`${t('share-release.send-message-optional')}`}
            </Tonality.UIText>

            <Tonality.UIText
              textStyle="body"
              color={theme.colors.neutralW90}
              style={{ marginLeft: 'auto', fontSize: '12px' }}
            >
              {messageCount}/{MAX_DESCRIPTION_LIMIT}
            </Tonality.UIText>
          </Row>

          <Row>
            <TextField
              id="message"
              name="message"
              ref={null}
              multiline
              maxLength={MAX_DESCRIPTION_LIMIT}
              wrapperStyles={{ paddingRight: 0, marginBottom: 24 }}
              inputStyles={{ fontSize: 14 }}
              placeholder={t('share-release.add-message-for-connections')}
              labelText=""
              errorStyle={{ color: theme.colors.error70 }}
              setting="player"
              onChange={onMessageChange}
              inputStyle={{
                borderColor: theme.colors.neutralW50
              }}
            />
          </Row>

          <Row>
            <Button
              variant="primary"
              setting="player"
              mode="dark"
              onClick={onShareReleaseSubmit}
              style={{ width: '100%' }}
            >
              {t('share-release.share-release')}
            </Button>
          </Row>
        </>
      ) : (
        <ShareReleaseModalEmptyState />
      )}
    </StyledModal>
  )
}

export default connect(mapStateToProps)(ShareReleaseModal)
